import type { AllergensCopy } from '../types/allergens';

export const allergensEs: AllergensCopy = {
  seo: {
    title: 'Alérgenos de la UE: los 14 y la ley, explicados',
    description:
      'Los 14 alérgenos de la UE y lo que la ley exige a los restaurantes, en lenguaje sencillo. Aprende a declarar los alérgenos en tu menú, además de las fuentes ocultas que suelen pillar por sorpresa en la cocina.',
  },
  hero: {
    eyebrow: 'Guía de campo para dueños de restaurantes',
    headingPre: 'Los 14 alérgenos de la UE, explicados en',
    headingScribble: 'lenguaje sencillo',
    body: 'Descubre qué alérgenos cubre la ley, dónde se esconden y cómo comunicárselo a tus clientes con claridad. Esta guía te lleva de la mano por los platos y por las normas, sin jerga legal.',
    factAllergenCount: '14 alérgenos',
    factRegulation: 'Reglamento (UE) n.º 1169/2011',
    factReadTime: '6 min de lectura',
    imageAlt: 'Una mesa de restaurante con un menú QR de SafestMenu',
  },
  intro: {
    leadPre: 'Si sirves comida en la UE, nombrar estos 14 alérgenos es a la vez',
    leadScribble1: 'obligatorio por ley',
    leadMid: 'y',
    leadScribble2: 'parte de una buena hospitalidad',
    leadPost: '.',
    body: 'Estos alérgenos se esconden en más sitios de los que la mayoría de los menús admite. Esta página explica los 14, la ley que hay detrás y cómo declararlos con claridad - sencillo y práctico, para que puedas ponerlo en marcha hoy mismo.',
    stats: [
      { big: '14', small: 'sustancias en una lista fija' },
      { big: 'Todos', small: 'los restaurantes, cafeterías y bares de la UE' },
      { big: 'Nombrados', small: 'con claridad, nunca al azar' },
    ],
  },
  whyItMatters: {
    eyebrow: 'Por qué importa',
    heading: 'Los clientes seguros son los que vuelven.',
    imageAlt: 'Clientes mirando juntos un menú en la mesa de un restaurante',
    cards: [
      {
        title: 'Una reacción puede ser grave',
        text: 'Para algunos clientes, una traza oculta no es cuestión de gustos - puede llevarlos al hospital.',
      },
      {
        title: 'La confianza es hospitalidad',
        text: 'Hazlo bien y la gente se relaja. Una información clara sobre alérgenos forma parte de una bienvenida cálida, no de la burocracia.',
      },
      {
        title: 'Es la ley',
        text: 'El restaurante es el operador de empresa alimentaria responsable, así que la obligación es tuya, no de tu proveedor.',
      },
    ],
  },
  theFourteen: {
    eyebrow: 'La referencia',
    heading: 'Conoce a los catorce',
    intro: 'Estos son los alérgenos que nombra el anexo II del Reglamento (UE) n.º 1169/2011. Es una lista fija y cerrada - no eliges cuáles controlar, y todo plato que contenga uno tiene que indicarlo.',
    gotchas: {
      heading: '3 que suelen pillar a las cocinas',
      items: [
        'El aceite de cacahuete siempre cuenta como cacahuete, esté refinado o no. Solo el aceite de soja totalmente refinado está exento, así que no apliques nunca esa excepción al aceite de cacahuete, de maní o de arachis.',
        'La avena es naturalmente sin gluten, pero casi siempre sufre contaminación cruzada durante la molienda. Trata la avena normal como si tuviera gluten, salvo que esté certificada sin gluten.',
        'La lecitina de soja (E322) está presente en gran parte de los chocolates, cremas para untar y productos de horno, así que revisa las fichas del proveedor y no solo los ingredientes visibles.',
      ],
    },
  },
  legislation: {
    eyebrow: 'La ley, en breve',
    heading: 'Reglamento (UE) n.º 1169/2011',
    paragraphs: [
      'Las normas proceden del Reglamento (UE) n.º 1169/2011, conocido a menudo como el Reglamento sobre información alimentaria facilitada al consumidor, o FIC. Al ser un reglamento de la UE, se aplica directamente en todos los Estados miembros, incluida Rumanía, sin necesidad de una versión nacional.',
      'Se aplica desde el 13 de diciembre de 2014. El artículo 9 enumera la información que debe llevar cualquier alimento, el artículo 21 regula cómo se resaltan los alérgenos en los alimentos envasados, y el anexo II es la lista de los 14 alérgenos.',
      'Para los alimentos servidos sin envasar - las comidas que cocinas por encargo - el artículo 44 hace obligatoria la información sobre alérgenos, pero deja el método a cada país. Las sanciones también se fijan a nivel nacional, según la normativa de seguridad alimentaria de cada país, no por el propio reglamento.',
    ],
    asksHeading: 'Qué te exige',
    asksItems: [
      'Nombra los 14 alérgenos que debes declarar',
      'Se aplica tanto a menús como a etiquetas',
      'Cubre los alimentos envasados y los servidos sin envasar',
      'Debe ser exacta y estar siempre actualizada',
    ],
    natashasLaw:
      'Una que quizá conozcas, la Ley de Natasha del Reino Unido, es una norma británica de 2021 - más estricta que el mínimo de la UE, y que no forma parte del derecho de la UE en absoluto.',
  },
  steps: {
    eyebrow: 'Cómo declararlo',
    heading: 'Convierte la obligación en una rutina sencilla',
    intro: 'Estos 4 pasos te llevan de un montón de recetas a una información sobre alérgenos en la que tus clientes pueden confiar.',
    items: [
      {
        title: 'Crea una única fuente de verdad',
        text: 'Marca cuáles de los 14 contiene cada plato, a partir de tus recetas y las fichas de tus proveedores. Guárdalo todo en un solo sitio para que cocina y sala digan siempre lo mismo.',
      },
      {
        title: 'Ponlo donde los clientes lo vean',
        text: 'Muéstralo por escrito por defecto: en el menú, en una tabla clara o en un menú digital. Lo escrito siempre gana a confiar en que alguien se acuerde.',
      },
      {
        title: 'Forma a tu equipo',
        text: 'El personal de sala debe leer el registro y saber cuándo consultar con cocina en lugar de adivinar. Respuestas exactas, siempre.',
      },
      {
        title: 'Mantenlo al día',
        text: 'Cualquier ajuste en una receta o un nuevo proveedor puede añadir o quitar un alérgeno. Actualiza el registro en el momento en que algo cambie.',
      },
    ],
    footnote: 'Revísalo cada vez que cambie una receta o un proveedor - ahí es donde la mayoría de los menús se quedan desactualizados sin que nadie se dé cuenta.',
  },
  comparison: {
    eyebrow: 'Dónde va la información',
    heading: 'Envasado frente a servido sin envasar',
    intro: 'Los mismos 14 alérgenos, declarados en distintos sitios según cómo se venda el alimento. Esta es la confusión que más vemos.',
    leftTitle: 'Alimentos envasados',
    leftPoints: [
      'Se vende en el envase en el que se colocó antes de pedirlo, como una salsa embotellada.',
      'Necesita una lista completa de ingredientes, con los 14 alérgenos resaltados en ella (artículo 21).',
      'También lleva el resto de datos de la etiqueta: nombre, cantidad y fecha de duración.',
    ],
    rightTitle: 'Alimentos servidos sin envasar',
    rightPoints: [
      'Las comidas que cocinas y sirves por encargo, más el alimento envasado en el local cuando un cliente lo pide.',
      'No se exige una lista de ingredientes impresa, pero la información sobre alérgenos es obligatoria (artículo 44).',
      'Se facilita como permita tu país, y siempre debe ser exacta y fácil de obtener antes de pedir.',
    ],
  },
  crossContact: {
    eyebrow: 'En la cocina',
    heading: 'La contaminación cruzada y el “puede contener”',
    imageAlt: 'Una superficie de preparación de restaurante limpia y recién desinfectada',
    bodyPre:
      'Declarar los alérgenos que son ingredientes es una tarea. La contaminación cruzada es otra - un alérgeno que pasa por accidente a través de freidoras, tablas o cuchillos compartidos. Una',
    bodyScribble: 'traza basta',
    bodyPost:
      ', así que el “puede contener” solo es honesto después de haber analizado el riesgo real y comprobar que no puedes eliminarlo.',
    tiles: [
      'Separa la preparación y los utensilios',
      'Limpia a fondo entre pedidos',
      'Forma a todo el equipo',
      'Sé honesto con el puede contener',
    ],
  },
  digital: {
    eyebrow: 'Dónde ayuda un menú QR',
    heading: 'Una edición, al día en cada mesa',
    body: 'Los menús impresos y las tablas plastificadas comparten un mismo punto débil: mantenerse al día. Un menú digital elimina ese problema. Etiquetas cada plato una vez, y un cambio llega a todos los clientes al instante - sin reimprimir.',
    list: [
      'Etiqueta los 14 alérgenos por plato, una sola vez',
      'Los clientes filtran el menú según lo que es seguro para ellos',
      'Siempre la versión actual, en su propio idioma',
    ],
    footnote: 'Siendo honestos con los límites: un menú QR no hace que tus datos sean correctos ni sustituye la formación del personal. Lo que soluciona es la información desactualizada, que es lo que más falla.',
    ctaLabel: 'Descubre cómo gestiona SafestMenu los alérgenos',
    qrImageAlt: 'Una tarjeta de mesa con código QR de SafestMenu junto a un café',
    dishImageAlt: 'Un plato emplatado mostrado en un menú digital de SafestMenu',
    journeyEyebrow: 'El recorrido de la información sobre alérgenos',
    journeyIntro: 'La información sobre alérgenos es un flujo con 4 traspasos. Tu menú - y una herramienta como SafestMenu - se sitúa justo en el punto en que llega al cliente.',
  },
  faq: {
    eyebrow: 'Preguntas frecuentes',
    heading: 'Respuestas rápidas',
    items: [
      {
        q: '¿Cuántos alérgenos tengo que declarar?',
        a: 'Los 14. La ley de la UE fija una lista cerrada en el anexo II del Reglamento 1169/2011, y todo plato que contenga uno tiene que indicarlo. Puedes señalar además otras cosas que te pregunten tus clientes, pero los 14 son el mínimo legal.',
      },
      {
        q: '¿Puedo simplemente decirles a los clientes que pregunten al personal?',
        a: 'Depende de tu país. Algunos Estados miembros permiten dar la información sobre alérgenos solo si se pregunta, siempre que un cartel claro indique a los clientes que deben preguntar y el personal dé respuestas exactas. Otros, como Francia, la exigen por escrito. Ponerlo por escrito es la opción más segura en todas partes.',
      },
      {
        q: '¿Se aplica la Ley de Natasha a mi restaurante?',
        a: 'Solo si estás en el Reino Unido. La Ley de Natasha es una norma británica del 1 de octubre de 2021 para los alimentos envasados en el propio local antes de que el cliente los pida, y es más estricta que el mínimo de la UE. No se aplica en Rumanía ni en el resto de la UE.',
      },
      {
        q: '¿Está exento de declaración el aceite de cacahuete?',
        a: 'No. El aceite de cacahuete cuenta como cacahuete esté refinado o no, así que siempre hay que declararlo. Solo el aceite de soja totalmente refinado tiene esa exención, y el aceite de maní o de arachis no son más que otros nombres del aceite de cacahuete.',
      },
      {
        q: '¿Quién es responsable si la información sobre alérgenos es incorrecta?',
        a: 'Tú. El restaurante es el operador de empresa alimentaria, así que la obligación de dar una información exacta es tuya, incluso si un proveedor cambió una receta sin avisarte. Por eso importa tener un único registro actualizado.',
      },
      {
        q: '¿Un menú QR me mantiene en regla por sí solo?',
        a: 'No, y no pretendemos decir lo contrario. Un menú digital mantiene tu información sobre alérgenos al día en todas las mesas a la vez, pero los datos solo son tan buenos como lo que introduzcas. Sigues necesitando personal formado y un proceso real de control de la contaminación cruzada en la cocina.',
      },
    ],
  },
  closingNote: {
    heading: 'Una nota sobre esta guía',
    body: 'Esto es una orientación general para ayudarte a organizarte, no asesoramiento legal. Las normas nacionales derivadas del artículo 44 varían dentro de la UE y se actualizan, así que consulta la normativa de donde operas o pregunta a tu autoridad nacional de seguridad alimentaria.',
  },
  emailCapture: {
    heading: 'Ten esta guía a mano',
    title: 'Recibe la lista de comprobación de alérgenos por correo',
    description: 'Te enviaremos esta guía y, de vez en cuando, notas prácticas sobre menús y etiquetado de alérgenos. Sin spam, puedes darte de baja cuando quieras.',
    buttonLabel: 'Envíamela',
  },
  cta: {
    heading: 'Pon tus alérgenos en un menú en el que tus clientes puedan confiar',
    body: 'Etiqueta los 14 alérgenos por plato, actualiza una vez y cada cliente verá el cambio en su mesa. Mantienes un único registro siempre al día, y los clientes filtran el menú según lo que es seguro para ellos.',
    buttonLabel: 'Prueba SafestMenu gratis',
  },
  diagrams: {
    journey: [
      { title: 'El plato', sub: 'Lo que realmente lleva, hasta la salsa' },
      { title: 'El registro', sub: 'Cada plato vinculado a los 14' },
      { title: 'El menú', sub: 'Mostrado al cliente, en papel o en pantalla', tag: 'SafestMenu' },
      { title: 'El cliente', sub: 'Toma una decisión en la que puede confiar' },
    ],
    routes: [{ label: 'En el menú' }, { label: 'En un cartel' }, { label: 'Al preguntar' }],
    orLabel: 'o',
    ingredients: {
      label: 'Ingredientes',
      segments: [
        { text: 'Harina de trigo', mark: true },
        { text: ', agua, aceite de colza, ' },
        { text: 'huevo', mark: true },
        { text: ', azúcar, ' },
        { text: 'leche', mark: true },
        { text: ', sal, malta de cebada.' },
      ],
      note: 'Alérgenos resaltados dentro de la lista de ingredientes.',
    },
  },
  reference: {
    ariaLabel: 'Los 14 alérgenos de la UE',
    allergenLabel: 'Alérgeno',
    commonlyIn: 'Habitual en',
    hidesIn: 'Se esconde en',
    remember: 'Recuerda',
    footnotePre: 'Toca cualquiera de los ',
    footnotePost: ' para ver dónde se esconde. Los ejemplos son un punto de partida - comprueba siempre tus propias recetas y las fichas de tus proveedores.',
  },
  items: {
    'cereals-gluten': {
      name: 'Gluten',
      short: 'Trigo, centeno, cebada, avena, espelta',
      common: 'Pan, pasta, masa de pizza, bizcochos, bollería, rebozados, pan rallado, cuscús, cerveza y la mayoría de salsas espesadas con harina.',
      hidden: 'La salsa de soja (normalmente fermentada con trigo), las pastillas de caldo y los granulados para salsas, el vinagre de malta, algunas salchichas y helados (con pan rallado o relleno de trigo), y las mezclas de especias.',
      note: 'La avena es sin gluten por naturaleza, pero casi siempre sufre contaminación cruzada durante la molienda. No trates un plato de avena simple como seguro para celíacos a menos que esté certificado sin gluten.',
    },
    crustaceans: {
      name: 'Crustáceos',
      short: 'Gambas, cangrejo, langosta, cigalas',
      common: 'Gambas, camarones, cangrejo, langosta, cigalas, cangrejos de río y langostinos.',
      hidden: 'La pasta de camarones (belacan) en curris tailandeses y del sudeste asiático, el caldo de marisco en sopas y risottos, los fondos de bullabesa y paella, y las galletas de gamba.',
      note: 'Una traza de caldo de gamba en una base de curry "vegetal" basta para desencadenar una reacción grave. Revisa woks, freidoras y ollas de caldo compartidos.',
    },
    eggs: {
      name: 'Huevo',
      short: 'Mayonesa, bizcochos, glaseados, natillas',
      common: 'Mayonesa, quiche, tortillas, bizcochos, merengue, pasta fresca con huevo, natillas, y alimentos rebozados o empanados.',
      hidden: 'El glaseado de huevo pincelado sobre bollería y pan, los consomés clarificados con clara de huevo, las mousses, las nubes de golosina y las coberturas de espuma en cócteles.',
      note: 'El baño de huevo en productos de horno es fácil de pasar por alto al leer un menú por encima. Señálalo en lugar de confiar en la descripción de la bollería.',
    },
    fish: {
      name: 'Pescado',
      short: 'Filetes, anchoas, salsas',
      common: 'Filetes de pescado, palitos de pescado, sushi, pastel de pescado, salmón ahumado y anchoas.',
      hidden: 'La salsa Worcestershire y el aliño César (anchoas), la salsa de pescado en platos tailandeses y vietnamitas, algunas salsas de pizza y pasta, las mezclas con salsa de ostras, y las pastillas de caldo.',
      note: 'La salsa de pescado y la salsa Worcestershire son las dos fuentes clásicas de pescado invisible que se cuelan en platos que, por lo demás, parecen de carne o de verdura.',
    },
    peanuts: {
      name: 'Cacahuetes',
      short: 'Maní, satay, aceite de arachis',
      common: 'Mantequilla de cacahuete, salsa satay, cacahuetes tostados y salados, aceite de cacahuete, y muchos platos asiáticos y africanos.',
      hidden: 'Las salsas de chile y curry enriquecidas con cacahuete molido, el chocolate y los postres elaborados en líneas compartidas, y la proteína vegetal hidrolizada.',
      note: '"Aceite de maní" y "aceite de arachis" no son más que el aceite de cacahuete con otro nombre, y ambos deben declararse. El aceite de cacahuete refinado no está exento - solo lo está el aceite de soja totalmente refinado.',
    },
    soybeans: {
      name: 'Soja',
      short: 'Salsa de soja, tofu, lecitina',
      common: 'Salsa de soja, tofu, edamame, miso, leche de soja y tempeh.',
      hidden: 'Muchas salsas y marinadas asiáticas (hoisin, teriyaki), las mezclas de aceites vegetales, los embutidos con relleno de soja, algunos panes, y la lecitina de soja (E322).',
      note: 'La lecitina de soja (E322) está presente en gran parte de los chocolates, cremas para untar y productos de horno. Revisa las fichas técnicas del proveedor, no solo el ingrediente visible.',
    },
    milk: {
      name: 'Leche',
      short: 'Queso, mantequilla, nata, suero',
      common: 'Leche, queso, mantequilla, nata, yogur y helado.',
      hidden: 'La leche en polvo en algunos panes y masas, los aglutinantes de proteína láctea en embutidos, el condimento de suero en las patatas fritas de bolsa, el caseinato sódico en coberturas "sin lácteos", y las salsas con roux.',
      note: 'La proteína láctea (caseína o suero) aparece en productos etiquetados como "sin lácteos" y en el pan. Nunca des por hecho que un pan simple o un adobo seco no llevan lácteos sin comprobarlo.',
    },
    'tree-nuts': {
      name: 'Frutos secos',
      short: 'Almendra, avellana, nuez, anacardo',
      common: 'Almendras, avellanas, nueces, anacardos, pecanas, nueces de Brasil, pistachos y macadamias - en bizcochos, pesto, aceites de frutos secos, bebidas de frutos secos y praliné.',
      hidden: 'El pesto y los aliños de ensalada, los aceites de frutos secos usados para freír o rematar un plato, el korma y otras salsas espesadas con frutos secos molidos, el mazapán y la frangipane, la granola, y los licores de frutos secos.',
      note: 'El café con leche de almendra y la pasta con pesto son dos descuidos fáciles de cometer. Ambos esconden frutos secos detrás de un nombre que no dice "fruto seco".',
    },
    celery: {
      name: 'Apio',
      short: 'Apionabo, caldo, condimentos',
      common: 'Ramas de apio, apionabo, sopas, guisos y ensaladas.',
      hidden: 'Las pastillas de caldo y el caldo concentrado (la sal de apio es una base habitual), los granulados para salsas, las mezclas de especias y sales condimentadas, algunas salchichas, y las salsas preparadas.',
      note: 'La sal de apio se esconde en casi todas las pastillas de caldo comerciales y mezclas de condimentos. Revisa el caldo base antes de dar por segura sin apio cualquier sopa o salsa.',
    },
    mustard: {
      name: 'Mostaza',
      short: 'Condimento, aliños, adobos',
      common: 'La mostaza como condimento, la mostaza en polvo, los aliños de ensalada y el piccalilli.',
      hidden: 'La mayonesa y muchas salsas compradas, las marinadas y adobos para barbacoa, los curris en polvo y mezclas de especias, algunos panes, y los embutidos.',
      note: 'La mostaza es un ingrediente sigiloso en la mayonesa y las marinadas. Rara vez aparece en el nombre del plato, pero está muy a menudo en la salsa.',
    },
    sesame: {
      name: 'Sésamo',
      short: 'Panecillos, tahini, hummus',
      common: 'Semillas de sésamo sobre pan y panecillos, tahini, hummus, halva, aceite de sésamo y falafel.',
      hidden: 'Las semillas horneadas en la parte inferior de los panecillos, las mezclas de especias (za’atar, dukkah), los aliños a base de tahini, y las freidoras compartidas con productos empanados en sésamo.',
      note: 'El sésamo suele hornearse en la parte inferior del panecillo, no solo espolvorearse por encima. Un vistazo rápido al pan puede pasarlo por alto.',
    },
    sulphites: {
      name: 'Dióxido de azufre y sulfitos',
      short: 'Vino, fruta seca, vinagre',
      common: 'El vino (especialmente blanco y rosado), la fruta seca como los orejones y las pasas, los refrescos de frutas, el zumo de fruta y la sidra.',
      hidden: 'Los embutidos y salchichas, las verduras en escabeche y en conserva, algunos productos de patata precortada y congelada, las salsas a base de vinagre, y algunos refrescos.',
      note: 'El umbral legal es una concentración superior a 10 mg/kg o 10 mg/L, no la mera presencia. Los orejones y el vino barato son las dos fuentes que más se olvidan.',
    },
    lupin: {
      name: 'Altramuz',
      short: 'Harina de altramuz, mezclas "sin"',
      common: 'La harina de altramuz y las semillas de altramuz (lupini), además de algunos panes y productos de horno sin gluten.',
      hidden: 'Las mezclas de harina de trigo y preparados de repostería que usan el altramuz como refuerzo de proteína, algunas pastas, los sustitutos veganos de la carne, y los panes y bollería de estilo continental.',
      note: 'El altramuz es poco frecuente en la cocina tradicional británica, pero habitual en los panes continentales y en las mezclas de harina sin gluten. Revisa cualquier harina importada o etiquetada "sin".',
    },
    molluscs: {
      name: 'Moluscos',
      short: 'Mejillones, ostras, calamar, caracoles',
      common: 'Mejillones, almejas, ostras, vieiras, calamar, pulpo y caracoles (escargot).',
      hidden: 'La salsa de ostras en salteados y marinadas, el caldo de marisco en bases de paella y risotto, la tinta de calamar que colorea pasta o arroz, y los extensores de surimi.',
      note: 'La salsa de ostras es el molusco oculto clásico. Aparece en salteados y marinadas que, por lo demás, parecen solo de carne o de verdura.',
    },
  },
};
